import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { MatchCategory } from '@peruselab/api-client/moleculeSearch';
import { Icon } from '../Icon';
import { ResultMatchDetails } from './ResultMatchDetails';
import { useLoadMoreOnApproach } from '../../hooks/useLoadMoreOnApproach';
import { MoleculeThumbnail, moleculeThumbnailSvg, paperPieceSvg } from '../MoleculeThumbnail';
import { usePaperPiece } from './paperPieces';
import { StructureZoomDialog, ZoomablePicture, type ZoomPicture } from '../StructureZoomDialog';
import { FavouriteHeart } from '../FavouriteHeart';
import type { SearchResultFilters } from '../../hooks/useSearchResultFilters';
import { ReactionArrowGlyph } from '../ReactionArrowGlyph';
import { useDragSource } from '../../dnd/DragDrop';
import type { PaperPlacement, StructureDragPayload } from '../../dnd/payloads';
import { usePaperAdditions } from './paperAdditions';
import { AddToPaperButton, useAddToPaper, useDismiss } from './AddToPaperButton';
import { ReactionResultCard } from './ReactionResultCard';
import { DrawingResultCard } from './DrawingResultCard';
import { moleculePagePath, usePublicAccess } from '../../features/public/publicAccess';
import { useAnnouncedMoleculeVisibility } from '../../features/chemistry/molecule/moleculeVisibilitySync';
import {
  CONTAINER_KINDS,
  type ContainerKind,
  type ContainerRef,
  type EntityType,
  type ResultCard
} from './searchResultsModel';

interface SearchResultsListProps {
  readonly filters: SearchResultFilters;
  readonly hasResults: boolean;
  // #523: dispatches on the card's own entity kind -- a drawing match opens straight into the
  // editor, but a reaction/illustration match has no editor representation to load into; the
  // caller decides what "open" means for each (WorkspaceShell routes those to a read-only viewer).
  readonly onOpen?: ((id: string, kind: EntityType) => void) | undefined;
  readonly openDisabled?: boolean | undefined;
  /** #743 -- leave out reaction cards (an arrow on the paper: the reaction search supplies them). */
  readonly hideReactions?: boolean | undefined;
}

// The result cards (one flat, score-sorted stream across every entity type -- no per-type
// grouping, no separate Identical lead block; each card's own CategoryBadge is the only
// category signal) plus the empty / filtered-out / "show more" affordances. Renders each card as
// a bare fragment child (no <ul> wrapper) so every card lands as a direct grid item of the
// parent `.workspace-mosaic` grid, alongside the drawing paper -- see WorkspaceShell.tsx. Split
// from the filter rail (ADR 0007 P6b) so the shell can put the rail in the FILTER tab and the
// list in the centre; SearchResultsPanel (/draw) still renders both together.
export function SearchResultsList({
  filters,
  hasResults,
  onOpen,
  openDisabled,
  hideReactions
}: SearchResultsListProps) {
  const { t } = useTranslation();
  const { rawCount, visibleCount, hasMore, visible } = filters;
  const allCards = hideReactions ? filters.allCards.filter((card) => card.kind !== 'reaction') : filters.allCards;
  // #684 -- continuous scrolling: the next batch is revealed as "Show more" nears the viewport
  // (the cards are already fetched; this only reveals them). The button stays for keyboard users.
  const showMoreRef = useLoadMoreOnApproach<HTMLButtonElement>(hasMore, visible, filters.showMore);

  return (
    <>
      {hasResults && rawCount === 0 ? <p>{t('searchResultsPanel.noMatches')}</p> : null}
      {hasResults && rawCount > 0 && visibleCount === 0 ? <p>{t('searchResultsPanel.allFilteredOut')}</p> : null}
      {/* #680 -- "N hidden by filters" lives in the toolbar's match badge ("51 of 124 matches"),
          not here: as a grid item it took a whole result cell beside the paper. */}

      {allCards.slice(0, visible).map((card) =>
        card.kind === 'reaction' ? (
          <ReactionResultCard
            key={card.key}
            reactionId={card.reactionId}
            title={card.label}
            scoreLabel={<CategoryScoreLabel category={card.category} score={card.score} />}
            why={card.matched ? t('reactionResults.contains', { molecule: card.matched }) : undefined}
            onOpen={onOpen ? (id) => onOpen(id, 'reactions') : undefined}
            openDisabled={openDisabled}
          />
        ) : card.kind === 'document' ? (
          <div key={card.key} className="search-result-card" data-testid="document-text-result-card">
            <div className="search-result-thumb">
              <Icon name="draw" />
            </div>
            <div className="search-result-card-footer">
              <span>{card.label}</span>
              <button
                type="button"
                disabled={openDisabled}
                onClick={() => onOpen?.(card.documentId, card.entityType)}
                aria-label={t('drawingResults.open', { title: card.label })}
              >
                <Icon name="openInNew" />
              </button>
            </div>
          </div>
        ) : card.kind === 'drawing' ? (
          <DrawingResultCard
            key={card.key}
            card={card}
            scoreLabel={<CategoryScoreLabel category={card.category} score={card.score} />}
            onOpen={onOpen ? (id) => onOpen(id, 'drawings') : undefined}
            openDisabled={openDisabled}
          />
        ) : (
          <ResultCardItem key={card.key} card={card} onOpen={onOpen} openDisabled={openDisabled} />
        )
      )}

      {hasMore ? (
        <button
          ref={showMoreRef}
          type="button"
          className="search-results-show-more icon-button"
          data-testid="search-results-show-more"
          onClick={filters.showMore}
          aria-label={t('searchResultsPanel.showMore')}
          title={t('searchResultsPanel.showMore')}
        >
          <Icon name="expandMore" />
        </button>
      ) : null}
    </>
  );
}

// #518 -- "Identical" alone, or "<Category>: <score>" for everything else; score is redundant
// once something is an exact match. Was a separate bordered badge + a plain score number, each
// its own flex row above the thumbnail; now one plain-text overlay label (see
// .search-result-card-overlay-top) so the thumbnail can use the whole card. #1161: "Same
// skeleton" alone too -- its score can be low (ferrocene's two forms: 0.17), reading as a poor match.
function CategoryScoreLabel({ category, score }: { readonly category: MatchCategory; readonly score: number }) {
  const { t } = useTranslation();
  const categoryLabel = t(`searchResultsPanel.categories.${category}`);
  return (
    <span className="search-result-card-label">
      {category === 'IDENTICAL' || category === 'SAME_SKELETON'
        ? categoryLabel
        : `${categoryLabel}: ${score.toFixed(2)}`}
    </span>
  );
}

function ResultCardItem({
  card,
  onOpen,
  openDisabled
}: {
  readonly card: ResultCard;
  readonly onOpen?: ((id: string, kind: EntityType) => void) | undefined;
  readonly openDisabled?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const { signedIn } = usePublicAccess();
  const additions = usePaperAdditions();
  const moleculeId = card.thumbnailMoleculeId;
  const paperPiece = usePaperPiece(moleculeId, card.category === 'IDENTICAL');
  // #1060: made public or private again since this search (here or in another tab) wins.
  const announced = useAnnouncedMoleculeVisibility(moleculeId);
  const isPrivate = announced ? announced === 'private' : card.private === true;
  const addMolecule = useMemo(
    () =>
      additions
        ? (isomerId?: string, placement?: PaperPlacement) => additions.addMolecule(moleculeId, isomerId, placement)
        : undefined,
    [additions, moleculeId]
  );
  const adding = useAddToPaper(addMolecule);
  // #699 -- the card drags onto the paper (or into a reaction box), landing where it is dropped
  // (the same flow as the button).
  const { add } = adding;
  const payload = useMemo<StructureDragPayload | null>(
    () =>
      additions ? { kind: 'structure', label: card.label, place: (placement) => void add(undefined, placement) } : null,
    [add, additions, card.label]
  );
  const dragSource = useDragSource(
    payload,
    <MoleculeThumbnail moleculeId={card.thumbnailMoleculeId} size={72} paperPiece={paperPiece} />
  );
  const [enlarged, setEnlarged] = useState(false);
  const picture = useMemo<ZoomPicture>(
    () => ({
      kind: 'svg',
      load: () =>
        paperPiece ? paperPieceSvg(paperPiece) : moleculeThumbnailSvg(card.thumbnailMoleculeId, card.highlight)
    }),
    [card.thumbnailMoleculeId, card.highlight, paperPiece]
  );
  return (
    <div className="search-result-card" data-testid="result-card" {...dragSource}>
      <div className="search-result-thumb">
        {/* #963: click or tap the picture to see it larger (a drag still drags the card). */}
        <ZoomablePicture name={card.label} onOpen={() => setEnlarged(true)}>
          {/* #940: a substructure hit shows where the query sits in it. */}
          <MoleculeThumbnail
            moleculeId={card.thumbnailMoleculeId}
            size={72}
            highlight={card.highlight}
            paperPiece={paperPiece}
          />
        </ZoomablePicture>
        <FavouriteHeart favourite={{ kind: 'molecule', id: card.thumbnailMoleculeId }} name={card.label} />
      </div>
      {enlarged ? (
        <StructureZoomDialog
          picture={picture}
          name={card.label}
          matchLabel={<CategoryScoreLabel category={card.category} score={card.score} />}
          monographHref={moleculePagePath(card.thumbnailMoleculeId, signedIn)}
          actions={
            additions ? (
              <AddToPaperButton
                moleculeId={card.thumbnailMoleculeId}
                adding={adding}
                boxes={additions.boxes}
                onAdded={() => setEnlarged(false)}
              />
            ) : null
          }
          onClose={() => setEnlarged(false)}
        />
      ) : null}
      <div className="search-result-card-overlay-top">
        <CategoryScoreLabel category={card.category} score={card.score} />
        {/* #1060: a molecule only the user's own space holds -- nobody else finds it. */}
        {isPrivate ? (
          <span
            className="search-result-private"
            data-testid="result-private"
            role="img"
            aria-label={t('moleculeVisibility.privateBadge')}
            title={t('moleculeVisibility.privateHint')}
          >
            <Icon name="lock" size="1rem" />
          </span>
        ) : null}
        {/* #1064: drawn, but in nothing saved yet -- it is kept, and saving confirms it. */}
        {card.provisional ? (
          <span
            className="search-result-provisional"
            data-testid="result-provisional"
            title={t('drawnMolecules.provisionalHint')}
          >
            {t('drawnMolecules.provisional')}
          </span>
        ) : null}
        {card.matchedBy ? <ResultMatchDetails text={card.matchedBy} testId="result-matched-by" /> : null}
        {card.category === 'SAME_SKELETON' ? (
          <ResultMatchDetails text={t('searchResultsPanel.sameSkeletonHint')} testId="result-same-skeleton" />
        ) : null}
      </div>
      <div className="search-result-card-footer">
        <span className="search-results-id" data-testid="result-name">
          {card.label}
        </span>
        {/* ADR 0017 D9 (#904): the molecule's factsheet, in a new tab so the search and the paper
            stay as they are (owner, 2026-10-10). */}
        <Link
          to={moleculePagePath(card.thumbnailMoleculeId, signedIn)}
          target="_blank"
          rel="noopener"
          className="result-container-trigger"
          data-testid="result-molecule-page"
          aria-label={t('moleculePage.open')}
          title={t('moleculePage.open')}
        >
          <Icon name="info" size="1.1rem" />
        </Link>
        {additions ? (
          <AddToPaperButton moleculeId={card.thumbnailMoleculeId} adding={adding} boxes={additions.boxes} />
        ) : null}
        {onOpen
          ? CONTAINER_KINDS.filter((kind) => card.containers[kind].length > 0).map((kind) => (
              <ContainerButton
                key={kind}
                kind={kind}
                refs={card.containers[kind]}
                onOpen={onOpen}
                openDisabled={openDisabled}
              />
            ))
          : null}
      </div>
    </div>
  );
}

const CONTAINER_ICON: Record<ContainerKind, () => ReactNode> = {
  drawings: () => <Icon name="draw" size="1.1rem" />,
  reactions: () => <ReactionArrowGlyph kind="forward" />,
  illustrations: () => <Icon name="image" size="1.1rem" />
};

/**
 * #688 -- where a result's molecule lives in the user's own work (legacy parity: the drawing
 * mark). One item opens straight away; several open a short picker of their titles.
 */
function ContainerButton({
  kind,
  refs,
  onOpen,
  openDisabled
}: {
  readonly kind: ContainerKind;
  readonly refs: readonly ContainerRef[];
  readonly onOpen: (id: string, kind: EntityType) => void;
  readonly openDisabled?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const untitled = t('searchResultsPanel.untitled');
  const single = refs.length === 1 ? refs[0]! : null;
  const label = single
    ? t(`searchResultsPanel.openIn.${kind}_one`, { title: single.title ?? untitled })
    : t(`searchResultsPanel.openIn.${kind}_other`, { count: refs.length });

  useDismiss(open, rootRef, () => setOpen(false));
  // #700 -- a lone drawing / illustration drags straight from the button.
  const singleDrag = useContainerDrag(kind, single);

  return (
    <div className="result-container-button" ref={rootRef}>
      <button
        type="button"
        className="result-container-trigger"
        data-testid={`result-open-${kind}`}
        aria-label={label}
        title={label}
        aria-expanded={single ? undefined : open}
        disabled={openDisabled}
        onClick={() => (single ? onOpen(single.id, kind) : setOpen((current) => !current))}
        {...singleDrag}
      >
        {CONTAINER_ICON[kind]()}
        {single ? null : <span className="result-container-count">{refs.length}</span>}
      </button>
      {open ? (
        <ul className="result-container-menu" aria-label={label}>
          {refs.map((ref) => (
            <li key={ref.id}>
              <ContainerMenuItem
                kind={kind}
                containerRef={ref}
                disabled={openDisabled}
                onOpen={() => {
                  setOpen(false);
                  onOpen(ref.id, kind);
                }}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * #700 -- a drawing or illustration listed on a card drags onto the paper (or into a reaction box):
 * dropped, its structures are added there. A reaction is not a structure to add, so it doesn't drag.
 */
function useContainerDrag(kind: ContainerKind, containerRef: ContainerRef | null) {
  const { t } = useTranslation();
  const additions = usePaperAdditions();
  const payload = useMemo<StructureDragPayload | null>(() => {
    if (!additions || !containerRef || kind === 'reactions') return null;
    const add = kind === 'drawings' ? additions.addDrawing : additions.addIllustration;
    return {
      kind: 'structure',
      label: containerRef.title ?? t('searchResultsPanel.untitled'),
      place: (placement) =>
        void add(containerRef.id, placement).catch((error: unknown) => console.error('Add to paper failed', error))
    };
  }, [additions, containerRef, kind, t]);
  return useDragSource(payload, <span className="dnd-ghost-chip">{CONTAINER_ICON[kind]()}</span>);
}

function ContainerMenuItem({
  kind,
  containerRef,
  disabled,
  onOpen
}: {
  readonly kind: ContainerKind;
  readonly containerRef: ContainerRef;
  readonly disabled?: boolean | undefined;
  readonly onOpen: () => void;
}) {
  const { t } = useTranslation();
  const drag = useContainerDrag(kind, containerRef);
  return (
    <button type="button" disabled={disabled} onClick={onOpen} {...drag}>
      {containerRef.title ?? t('searchResultsPanel.untitled')}
    </button>
  );
}
